import { useMemo, useState } from 'react';
import { X, Printer, FileText, LayoutGrid, Rows2, Grid2x2 } from 'lucide-react';
import { useEvents } from '../../hooks/useEvents';
import { useChecklistItemsForDateRange } from '../../hooks/useEventOps';
import { usePlanningTodosForDateRange } from '../../hooks/usePlanning';
import {
  buildCalendarPrintHtml,
  monthsInRange,
  CALENDAR_PRINT_FORMATS,
  type CalendarPrintFormat,
} from '../../utils/calendarPrint';

const MAX_RANGE_MONTHS = 24;

const FORMAT_ICONS: Record<CalendarPrintFormat, typeof LayoutGrid> = {
  ONE_PER_PAGE: LayoutGrid,
  TWO_PER_PAGE: Rows2,
  FOUR_PER_PAGE: Grid2x2,
  LIST: FileText,
};

interface Props {
  /** Initial month shown in the range pickers (0-based month). */
  initialYear: number;
  initialMonth: number;
  /** Initial state of the "hide completed" toggle (mirrors the calendar page setting). */
  initialHideDone?: boolean;
  onClose: () => void;
}

function ymToInputValue(year: number, month: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}`;
}

function parseInputValue(value: string): { year: number; month: number } | null {
  const [y, m] = value.split('-').map(Number);
  if (!y || !m || m < 1 || m > 12) return null;
  return { year: y, month: m - 1 };
}

export function CalendarPrintModal({
  initialYear,
  initialMonth,
  initialHideDone = false,
  onClose,
}: Props) {
  const [fromValue, setFromValue] = useState(ymToInputValue(initialYear, initialMonth));
  const [toValue, setToValue] = useState(ymToInputValue(initialYear, initialMonth));
  const [format, setFormat] = useState<CalendarPrintFormat>('ONE_PER_PAGE');
  const [hideDone, setHideDone] = useState(initialHideDone);

  const from = parseInputValue(fromValue);
  const to = parseInputValue(toValue);

  const months = useMemo(() => {
    const f = parseInputValue(fromValue);
    const t = parseInputValue(toValue);
    return f && t ? monthsInRange(f.year, f.month, t.year, t.month) : [];
  }, [fromValue, toValue]);

  const rangeError =
    !from || !to
      ? 'Select a valid month range.'
      : months.length === 0
        ? '"From" month must be before or equal to "To" month.'
        : months.length > MAX_RANGE_MONTHS
          ? `Range is limited to ${MAX_RANGE_MONTHS} months.`
          : null;

  const validRange = rangeError === null && from !== null && to !== null;

  const dateFrom = from ? `${from.year}-${String(from.month + 1).padStart(2, '0')}-01` : '';
  const dateTo = to
    ? `${to.year}-${String(to.month + 1).padStart(2, '0')}-${String(
        new Date(to.year, to.month + 1, 0).getDate(),
      ).padStart(2, '0')}`
    : '';

  const { data: events = [], isLoading: eventsLoading } = useEvents();
  const { data: taskData, isLoading: tasksLoading } = useChecklistItemsForDateRange(
    dateFrom,
    dateTo,
    validRange,
  );
  const { data: planningData, isLoading: planningLoading } = usePlanningTodosForDateRange(
    dateFrom,
    dateTo,
    validRange,
  );

  const isLoading = eventsLoading || (validRange && (tasksLoading || planningLoading));

  function handlePrint() {
    if (!validRange || !from || !to) return;

    const tasks = (taskData?.items ?? []).filter((t) => !hideDone || !t.isDone);
    const planning = (planningData?.items ?? []).filter((t) => !hideDone || !t.isDone);

    const html = buildCalendarPrintHtml({
      fromYear: from.year,
      fromMonth: from.month,
      toYear: to.year,
      toMonth: to.month,
      format,
      events,
      tasks,
      planning,
    });

    const win = window.open('', '_blank', 'width=1100,height=800');
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 400);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
          <h2 className="text-base font-semibold text-gray-900">Print Calendar</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
          {/* Month range */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Month Range
            </p>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <label htmlFor="print-from-month" className="block text-xs text-gray-500 mb-1">
                  From
                </label>
                <input
                  id="print-from-month"
                  type="month"
                  value={fromValue}
                  onChange={(e) => setFromValue(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="flex-1">
                <label htmlFor="print-to-month" className="block text-xs text-gray-500 mb-1">
                  To
                </label>
                <input
                  id="print-to-month"
                  type="month"
                  value={toValue}
                  onChange={(e) => setToValue(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            {rangeError ? (
              <p className="text-xs text-red-600 mt-1.5">{rangeError}</p>
            ) : (
              <p className="text-xs text-gray-400 mt-1.5">
                {months.length} month{months.length === 1 ? '' : 's'} selected
              </p>
            )}
          </div>

          {/* Format */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Format
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {CALENDAR_PRINT_FORMATS.map((f) => {
                const Icon = FORMAT_ICONS[f.id];
                const selected = format === f.id;
                return (
                  <button
                    key={f.id}
                    onClick={() => setFormat(f.id)}
                    className={`flex items-start gap-2.5 p-3 rounded-lg border text-left transition-colors ${
                      selected
                        ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    aria-pressed={selected}
                  >
                    <Icon
                      size={18}
                      className={`shrink-0 mt-0.5 ${selected ? 'text-primary-600' : 'text-gray-400'}`}
                    />
                    <span>
                      <span
                        className={`block text-sm font-medium ${selected ? 'text-primary-700' : 'text-gray-800'}`}
                      >
                        {f.label}
                      </span>
                      <span className="block text-xs text-gray-500 mt-0.5">{f.description}</span>
                      <span className="inline-block mt-1 text-[10px] uppercase tracking-wide font-semibold text-gray-400">
                        {f.orientation}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Options */}
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={hideDone}
              onChange={(e) => setHideDone(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Hide completed tasks
          </label>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 font-medium text-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={handlePrint}
            disabled={!validRange || isLoading}
            className="flex items-center gap-1.5 px-4 py-2 bg-gray-800 text-white rounded-md text-sm font-medium hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Printer size={14} />
            {isLoading ? 'Loading…' : 'Print / Save PDF'}
          </button>
        </div>
      </div>
    </div>
  );
}
